/**
 * Thread & patient-aware conversation service.
 * Single source of truth for threads and messages; persistence in localStorage.
 */

import { clearActiveStream as clearActiveStreamRecord } from './streamResumeStore';

const STORAGE_KEY = 'clinician_threads';
const SCHEMA_VERSION = 2;

// ----- Per-user storage scoping -----
//
// Thread history used to persist under ONE shared localStorage key, so on a
// shared hospital workstation every clinician who signed in on that browser
// saw the previous clinician's conversations. The store is now keyed by the
// authenticated user id; the legacy shared key is deliberately never read
// once a user id is known (its contents cannot be attributed to a user).
let activeUserScope = '';

export function setActiveThreadUser(userId: string | null | undefined): boolean {
  const next = String(userId ?? '').trim();
  if (next === activeUserScope) return false;
  activeUserScope = next;
  return true;
}

function storageKey(): string {
  return activeUserScope ? `${STORAGE_KEY}:${activeUserScope}` : STORAGE_KEY;
}

// ----- Types -----

export interface ThreadItem {
  id: string;
  title: string;
  timestamp: string;
  patientId?: string;
  patientName?: string;
}

export interface ThinkingStep {
  id: string;
  label: string;
  status: 'running' | 'done' | 'failed';
  timestamp: string;
  toolName?: string;
  detail?: string;
}

export interface ResponseMetadata {
  tools_used?: string[];
  confidence_score?: number;
  processing_time_ms?: number;
  /** #180: client-measured end-to-end elapsed (submit -> final metadata commit),
   * via performance.now() in useCommandStream. This is what the footer clock
   * displays; processing_time_ms (server-reported) stays available as a
   * secondary/tooltip figure. */
  client_processing_time_ms?: number;
  intent_type?: string;
  execution_flow?: string;
  agentic_core_adk_response_contract?: string;
  disclaimer?: string;
  session_id?: string;
  conversation_id?: string;
  structured?: Record<string, unknown>;
  code_references?: Array<{ term: string; code: string; system: string }>;
  rag_sources?: Array<{
    source: string;
    content_preview?: string;
    resource?: string;
    chunk_id?: string;
    document_id?: string;
    score?: string;
  }>;
  rag_spans?: Array<{ start: number; end: number; resource: string }>;
  rag_grounding?: {
    status?: string;
    source_count?: number;
    unavailable_reason?: string | null;
    tool?: string;
  };
  // Citation-free per-branch guideline evidence contract (CMA + Knowledge RAG).
  // Sole driver of GuidelineEvidencePanel. Kept as a defensively-narrowed
  // passthrough object — the panel does its own field-level narrowing — so a
  // shape change on the wire degrades to "panel absent", never a crash.
  guideline_evidence?: {
    categories?: Array<Record<string, unknown>>;
    references?: Array<Record<string, unknown>>;
  } & Record<string, unknown>;
  suppress_clinical_references?: boolean;
  router_overview?: string;
  router_overview_payload?: Record<string, unknown>;
  specialty_output_mode?: string;
  specialty_outputs?: Array<Record<string, unknown>>;
  specialty_routing?: Record<string, unknown> | string;
  dual_specialty?: boolean;
  engaged_specialties?: string[];
  skipped_specialties_without_rag?: string[];
  suggested_next_questions?: string[];
  /** #177: authoritative S1 text replacing the streamed preview at finalization. */
  authoritative_final_text?: string;
  /** Backend answer-contract gate (optional). */
  evidence_scope?: string;
  answer_contract_evaluated?: boolean;
  answer_contract_passed?: boolean;
  answer_contract_reasons?: string[];
  /** Scope-policy heuristic: user message may need extra caution. */
  high_risk_user_prompt?: boolean;
  high_risk_hits?: string[];
  /** Post-synthesis rubric loop (MARCH_CLINICAL_QUALITY_REFINEMENT). */
  clinical_thinking_flow?: Array<{ step?: string; title?: string; body?: string }>;
  clinical_rubric_scores?: Record<string, number>;
  clinical_quality_iterations?: number;
  clinical_quality_gate_satisfied?: boolean | null;
  clinical_quality_policy?: string;
  clinical_quality_refinement_skipped?: string;
  /** Same as skipped code when refinement was skipped (explicit API field). */
  clinical_quality_refinement_skipped_reason?: string;
  clinical_stability_warnings?: string[];
  clinical_residual_risk_summary?: string;
  clinical_hard_constraints_met?: boolean;
  clinical_soft_targets_met?: boolean;
  clinical_regression_detected?: boolean;
  clinical_quality_plateau_detected?: boolean;
  /** Derived 0–1 confidence from rubric mean minus penalties (not a clinical certainty claim). */
  clinical_confidence_score?: number;
  /** Post-synthesis rubric loop completed; hide redundant answer_contract footer noise. */
  clinical_quality_layer_authoritative?: boolean;
  /** Terminal layer: passed | provisional | failed | skipped */
  clinical_quality_gate_status?: string;
}

export interface StoredMessage {
  id: string;
  role: 'user' | 'assistant';
  content?: string;
  answer?: string;
  thinkingText?: string;
  thinkingSteps?: ThinkingStep[];
  thinkingComplete?: boolean;
  isStreaming?: boolean;
  streamPhase?: 'thinking' | 'final';
  evidence?: Record<string, unknown>;
  responseMetadata?: ResponseMetadata;
  suggestions?: string;
  widget?: { type: string; data: Record<string, unknown> };
  widgets?: Array<{ type: string; data: Record<string, unknown> }>;
  retryCommand?: string;
  // #75: true when this assistant turn was rebuilt from the server's
  // text-only archive — its structured sections/references/chips are gone,
  // and the UI must disclose that rather than silently degrade.
  restoredTextOnly?: boolean;
  timestamp: string;
}

export type MessageWithDate = Omit<StoredMessage, 'timestamp'> & { timestamp: Date };

export interface ThreadState {
  version: number;
  currentThreadId: string;
  threads: ThreadItem[];
  messagesByThread: Record<string, StoredMessage[]>;
}

export interface PatientInfo {
  name: string;
  mrn: string;
}

// ----- Persistence -----

function getDefaultState(): ThreadState {
  return {
    version: SCHEMA_VERSION,
    currentThreadId: '',
    threads: [],
    messagesByThread: {},
  };
}

export function formatThreadTitle(patientLabel: string, date: Date): string {
  const createdLabel = date.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
  return `${patientLabel || 'No patient selected'} – ${createdLabel}`;
}

function migrate(state: ThreadState): ThreadState {
  // Normalize legacy "empty" default thread (no patient, no messages) to a truly empty state
  if (
    state.threads &&
    state.threads.length === 1
  ) {
    const only = state.threads[0];
    const msgs = state.messagesByThread?.[only.id] ?? [];
    const noPatient =
      !only.patientId &&
      (!only.patientName || only.patientName === 'No patient selected');
    const noMessages = !Array.isArray(msgs) || msgs.length === 0;
    const isPlaceholder =
      only.title === 'New conversation' &&
      noPatient &&
      noMessages;
    if (isPlaceholder) {
      return getDefaultState();
    }
  }

  if (state.version === SCHEMA_VERSION) return state;
  return getDefaultState();
}

/**
 * Self-heal stale streaming flags. No stream survives a navigation or reload,
 * but a message persisted mid-stream keeps ``isStreaming: true`` — and every
 * final-answer render path requires ``!isStreaming``, so the clinician's last
 * exchange looked "missing" (dead spinner hiding an already-saved answer).
 * Finalizing at load time unhides every such message, past and future.
 */
function healInterruptedMessages(byThread: Record<string, StoredMessage[]>): Record<string, StoredMessage[]> {
  const healed: Record<string, StoredMessage[]> = {};
  for (const [threadId, messages] of Object.entries(byThread)) {
    healed[threadId] = (messages ?? []).map((m) => {
      if (!m?.isStreaming) return m;
      // #74 (Gutmann round, PROVEN live req-cf368023): a SUCCESSFUL structured
      // response persists with answer === '' BY DESIGN (useCommandStream's
      // onDone blanks the raw text whenever widgets/structured content carry
      // the answer, so the UI never shows data twice). The old check read
      // only answer/content, so it was false precisely for the responses
      // that SUCCEEDED — and the heal then overwrote a complete review with
      // "This response was interrupted". A message is content-bearing when
      // ANY content field is populated; and a truly interrupted message is
      // ANNOTATED, never destroyed (the stored record may still hold
      // recoverable fields).
      const meta = (m as { responseMetadata?: { structured?: unknown } }).responseMetadata;
      const hasContent = Boolean(
        String(m.answer ?? '').trim() ||
          String(m.content ?? '').trim() ||
          (meta && meta.structured) ||
          (m as { widget?: unknown }).widget ||
          (Array.isArray((m as { widgets?: unknown[] }).widgets) &&
            ((m as { widgets?: unknown[] }).widgets as unknown[]).length > 0) ||
          (m as { evidence?: unknown }).evidence
      );
      return {
        ...m,
        isStreaming: false,
        streamPhase: undefined,
        answer: hasContent
          ? m.answer
          : 'This response was interrupted (the page was closed or navigated away before any content arrived). Please ask again.',
      };
    });
  }
  return healed;
}

// ----- Storage area (SEC-04) -----
//
// Transcripts contain clinical content (PHI). They are cached tab-scoped in
// sessionStorage — gone when the tab closes — never persisted to disk-backed
// localStorage where they would outlive the login on a shared hospital
// workstation. Threads remain durable server-side (owner-scoped
// /v1/conversations); this store is only a per-tab working cache.

function storageArea(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.sessionStorage;
  } catch {
    return null;
  }
}

/** Delete every thread key (any user scope) from one storage area. */
function purgeThreadKeysFrom(store: Storage | null): void {
  if (!store) return;
  try {
    const doomed: string[] = [];
    for (let i = 0; i < store.length; i += 1) {
      const key = store.key(i);
      if (key && (key === STORAGE_KEY || key.startsWith(`${STORAGE_KEY}:`))) doomed.push(key);
    }
    doomed.forEach((key) => store.removeItem(key));
  } catch {
    // best-effort
  }
}

/** One-time cleanup: remove transcripts persisted by older builds to localStorage. */
function purgeLegacyLocalStorageTranscripts(): void {
  try {
    purgeThreadKeysFrom(typeof window === 'undefined' ? null : window.localStorage);
  } catch {
    // best-effort
  }
}

/** SEC-04: wipe the transcript cache everywhere (called on logout). */
export function purgeAllThreadStorage(): void {
  purgeThreadKeysFrom(storageArea());
  purgeLegacyLocalStorageTranscripts();
  // Refresh-resume marker rides the same PHI posture as the transcripts.
  // force: logout must wipe the marker even mid-teardown (PHI posture wins
  // over resume convenience).
  clearActiveStreamRecord(undefined, { force: true });
}

export function load(): ThreadState {
  purgeLegacyLocalStorageTranscripts();
  try {
    const raw = storageArea()?.getItem(storageKey());
    if (!raw) return getDefaultState();
    const parsed = JSON.parse(raw) as ThreadState;
    if (!parsed.threads || !parsed.messagesByThread) return getDefaultState();
    const state: ThreadState = {
      version: parsed.version ?? 1,
      currentThreadId: parsed.currentThreadId || parsed.threads[0]?.id || `thread-${Date.now()}`,
      threads: parsed.threads,
      messagesByThread: healInterruptedMessages(parsed.messagesByThread),
    };
    return migrate(state);
  } catch {
    return getDefaultState();
  }
}

export function save(state: ThreadState): void {
  try {
    storageArea()?.setItem(storageKey(), JSON.stringify(state));
  } catch {
    // ignore quota
  }
}

// ----- Message serialization -----

export function serializeMessages(messages: MessageWithDate[]): StoredMessage[] {
  return messages.map((m) => ({
    ...m,
    timestamp: (m.timestamp as Date).toISOString(),
  })) as StoredMessage[];
}

export function deserializeMessages(messages: StoredMessage[]): MessageWithDate[] {
  return messages.map((m) => ({ ...m, timestamp: new Date(m.timestamp) }));
}

// ----- Pure state transitions -----

export function createThread(
  state: ThreadState,
  opts: { patientName?: string; patientId?: string; id?: string } = {}
): ThreadState {
  const id = opts.id || `thread-${Date.now()}`;
  const now = new Date();
  const thread: ThreadItem = {
    id,
    title: 'New conversation',
    timestamp: now.toISOString(),
    patientId: opts.patientId,
    patientName: opts.patientName?.trim() || undefined,
  };
  return {
    ...state,
    version: SCHEMA_VERSION,
    threads: [...state.threads, thread],
    messagesByThread: { ...state.messagesByThread, [id]: [] },
    currentThreadId: id,
  };
}

export function selectThread(state: ThreadState, threadId: string): ThreadState {
  if (state.currentThreadId === threadId) return state;
  const exists = state.threads.some((t) => t.id === threadId);
  if (!exists) return state;
  return { ...state, currentThreadId: threadId };
}

export function addMessage(state: ThreadState, threadId: string, msg: MessageWithDate): ThreadState {
  let s = state;
  if (!threadId || !s.threads.some((t) => t.id === threadId)) {
    s = createThread(s);
    threadId = s.currentThreadId;
  }
  const list = deserializeMessages(s.messagesByThread[threadId] ?? []);
  const next = [...list, msg];
  return {
    ...s,
    messagesByThread: { ...s.messagesByThread, [threadId]: serializeMessages(next) },
  };
}

export function setThreadMessages(state: ThreadState, threadId: string, messages: MessageWithDate[]): ThreadState {
  return {
    ...state,
    messagesByThread: { ...state.messagesByThread, [threadId]: serializeMessages(messages) },
  };
}

export function deleteThread(state: ThreadState, threadId: string): ThreadState {
  const threads = state.threads.filter((t) => t.id !== threadId);
  if (threads.length === 0) return getDefaultState();
  const next: ThreadState = {
    ...state,
    threads,
    messagesByThread: { ...state.messagesByThread },
    currentThreadId: state.currentThreadId === threadId ? threads[0].id : state.currentThreadId,
  };
  delete next.messagesByThread[threadId];
  return next;
}

export function renameThread(state: ThreadState, threadId: string, title: string): ThreadState {
  return {
    ...state,
    threads: state.threads.map((t) => (t.id === threadId ? { ...t, title } : t)),
  };
}

/** Command or phrase -> short label for thread title */
const CONTEXT_LABELS: Record<string, string> = {
  '/summary': 'Summary',
  '/latest_assessment': 'Latest assessment',
  '/risk_profile': 'Risk factors',
  '/risk_drivers': 'Risk factors',
  '/risk_mitigation': 'Risk mitigation',
  '/risk_compare': 'Risk compare',
  '/alerts': 'Alerts',
  '/twin_snapshot': 'Twin snapshot',
  '/draft_note': 'Draft note',
  '/draft_plan': 'Draft plan',
  '/simulate': 'Simulation',
  '/export_pdf': 'Export PDF',
  '/export_csv': 'Export CSV',
  '/revise_note': 'Revise note',
  '/assessment_trend': 'Assessment trend',
  '/redflags': 'Red flags',
  '/fhir_patient': 'FHIR patient',
};

export function renameThreadFromFirstMessage(state: ThreadState, threadId: string, firstUserContent: string): ThreadState {
  const trimmed = firstUserContent.trim();
  if (!trimmed) return state;
  const lower = trimmed.toLowerCase();
  const base = lower.split(/\s+/)[0];
  const label = CONTEXT_LABELS[base] ?? CONTEXT_LABELS[base.startsWith('/') ? base : '/' + base];
  const raw = trimmed.slice(0, 48).replace(/\n/g, ' ').trim() || 'Conversation';
  const title = label || (trimmed.length > 48 ? raw + '…' : raw);
  return renameThread(state, threadId, title);
}

export function updateThreadPatient(
  state: ThreadState,
  threadId: string,
  patient: { name: string; mrn?: string }
): ThreadState {
  const thread = state.threads.find((t) => t.id === threadId);
  if (!thread) return state;
  const created = new Date(thread.timestamp);
  const title = formatThreadTitle(patient.name, created);
  return {
    ...state,
    threads: state.threads.map((t) =>
      t.id === threadId ? { ...t, title, patientName: patient.name, patientId: patient.mrn } : t
    ),
  };
}

// ----- Helpers -----

export function getMessagesForThread(state: ThreadState, threadId: string): MessageWithDate[] {
  return deserializeMessages(state.messagesByThread[threadId] ?? []);
}

export function getCurrentThread(state: ThreadState): ThreadItem | undefined {
  return state.threads.find((t) => t.id === state.currentThreadId);
}

/** Threads visible in list (exclude generic "New thread" placeholder from old schema) */
export function getVisibleThreads(state: ThreadState): ThreadItem[] {
  return state.threads
    .filter((t) => t.title !== 'New thread')
    .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
}

/** Group threads by patient label for UI */
export function getThreadsByPatient(state: ThreadState): { patientLabel: string; threads: ThreadItem[] }[] {
  const byPatient = new Map<string, ThreadItem[]>();
  for (const t of getVisibleThreads(state)) {
    const key = t.patientName?.trim() || 'No patient selected';
    if (!byPatient.has(key)) byPatient.set(key, []);
    byPatient.get(key)!.push(t);
  }
  return Array.from(byPatient.entries()).map(([patientLabel, threads]) => ({ patientLabel, threads }));
}


// ----- Server-backed history (owner-scoped /v1/conversations) -----

export interface ServerConversationSummary {
  conversation_id: string;
  patient_id: string | null;
  title: string | null;
  updated_at: string | null;
}

/**
 * Merge the server's owner-scoped conversation list into local state. The
 * server list is authoritative for WHICH threads exist for this clinician
 * (cross-device, survives cleared storage); local state keeps message bodies
 * it already has. Never touches the current selection.
 */
export function mergeServerConversations(
  state: ThreadState,
  conversations: ServerConversationSummary[],
): ThreadState {
  const known = new Set(state.threads.map((t) => t.id));
  const additions: ThreadItem[] = [];
  for (const conv of conversations) {
    const id = String(conv.conversation_id || '').trim();
    if (!id || known.has(id)) continue;
    additions.push({
      id,
      title: (conv.title || '').trim() || 'Conversation',
      timestamp: conv.updated_at || new Date().toISOString(),
      patientId: conv.patient_id || undefined,
    });
  }
  if (additions.length === 0) return state;
  const messagesByThread = { ...state.messagesByThread };
  for (const t of additions) messagesByThread[t.id] = messagesByThread[t.id] ?? [];
  return {
    ...state,
    threads: [...state.threads, ...additions],
    messagesByThread,
  };
}

/** Map server turns to renderable messages for restoring an empty thread.
 *
 * #75 (Gutmann round): the server conversation store keeps TEXT ONLY, so a
 * restored assistant turn has lost its structured sections, references and
 * chips — and for a structured response whose raw text was deliberately
 * blanked (useCommandStream onDone), the surviving text may be EMPTY. Restored
 * turns are therefore marked `restoredTextOnly` so the UI can present an
 * explicit text-only-archive affordance instead of silently degrading, and an
 * empty assistant turn states what happened instead of rendering as a blank
 * bubble. Persisting the structured payload server-side is the follow-up that
 * removes this limitation; until then the degradation is DISCLOSED, never
 * silent.
 */
export function messagesFromServerTurns(
  turns: Array<{ role: string; content: string; created_at: string | null }>,
): MessageWithDate[] {
  return turns.map((turn, index) => {
    const role: 'user' | 'assistant' = turn.role === 'user' ? 'user' : 'assistant';
    const base = {
      id: `restored-${index}-${turn.created_at ?? ''}`,
      role,
      timestamp: turn.created_at ? new Date(turn.created_at) : new Date(),
    };
    if (role === 'user') {
      return { ...base, content: turn.content };
    }
    const text = String(turn.content ?? '').trim();
    return {
      ...base,
      restoredTextOnly: true,
      answer:
        text ||
        'This earlier answer was a structured review; its formatted content is not kept in the text archive this device restored from. Ask again to regenerate it.',
    };
  }) as MessageWithDate[];
}
